import type { InjectionKey } from 'vue'
import type { RequestLogLevel, ResourceLogging } from '@/backend/ingresses.types'

export type ResourceFamily = 'ingress' | 'egress'

/** What narrows the grid. Mutable, so the toolbar can bind to it. */
export interface LoggingFilters {
  family: ResourceFamily | 'all'
  overriddenOnly: boolean
  search: string
}


/** One row of the Settings grid: a resource and the logging override stored against it. */
export interface LoggingRow extends ResourceLogging {
  resourceId: string
  family: ResourceFamily
  /** False for a resource the caller may read but not write; the row is shown read-only. */
  canWrite: boolean
}

/**
 * The deployment's defaults and ceilings, from `system/trafficLogging/*`.
 *
 * Reading them needs `registry:read`, which a Settings-capable user may not hold — so every field is
 * optional and the tab degrades to server-side validation rather than refusing to render.
 */
/**
 * In hours, while the per-resource overrides the grid edits are in days.
 *
 * The deployment sets these per tenant, and a tenant that should barely log at all — a free
 * development instance — needs a ceiling shorter than the smallest value the day-valued override can
 * express. The grid converts for display; see `SettingsTab.vue`.
 */
export interface LoggingCeilings {
  defaultLevel?: RequestLogLevel
  metadataHours?: number
  maxMetadataHours?: number
  fullHours?: number
  maxFullHours?: number
}

/**
 * The Settings tab's view state: the grid's rows, its filters and its save lifecycle.
 *
 * A view context rather than a Pinia store because the filters and the pending edits are per-view.
 * Two Monitoring views sharing one `SpreadsheetChangeset` would show each other's unsaved edits and
 * save them from whichever tab pressed the button.
 *
 * The resource lists themselves come from the `resources` Pinia store, which is a genuine
 * single-instance cache; only the overrides being edited live here.
 */
export interface LoggingViewContext {
  readonly rows: LoggingRow[]

  /** `rows` narrowed by the family, search and overridden-only filters. */
  readonly filteredRows: LoggingRow[]

  readonly ceilings: LoggingCeilings

  /** True when the ceilings could not be read, which the tab reports rather than hides. */
  readonly ceilingsUnavailable: boolean

  readonly filters: LoggingFilters

  get workState(): Readonly<{
    isLoading: boolean
    isSaving: boolean
    error: string | null
  }>

  refresh(): Promise<void>


  /** Writes one resource's changed fields; an explicit null clears that override. */
  save(resourceId: string, family: ResourceFamily, patch: ResourceLogging): Promise<void>

  setSaving(value: boolean): void
}

export const loggingViewContextKey: InjectionKey<LoggingViewContext> = Symbol('loggingViewContext')
